import { useState, useMemo } from "react";
import { ChevronDown, Search, Check, X } from "lucide-react";

import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-2 — MultiSelectFilter genérico.
//
// Props:
//   label, placeholder
//   options: [{ value, label, icon?, count?, tone? }]
//   selected: string[] (controlled)
//   onChange: (string[]) => void
//   searchable: boolean
//   testId
//
// Renderiza o trigger como pill com count selecionado. Popover lista
// opções com checkbox visual + busca opcional.

export default function MultiSelectFilter({
  label,
  placeholder = "Selecionar...",
  options = [],
  selected = [],
  onChange,
  searchable = true,
  testId = "multiselect",
  align = "start",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o =>
      String(o.label || o.value).toLowerCase().includes(q)
    );
  }, [options, query]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const count = selected.length;
  const summary = count > 0
    ? `${label}: ${count}`
    : label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-caption font-medium border transition-all duration-fast ease-smooth",
            count > 0
              ? "bg-brand-cyan/10 border-brand-cyan/30 text-brand-cyan"
              : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
          )}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate max-w-[140px]">{summary}</span>
          {count > 0 ? (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clear(e); }}
              className="inline-flex items-center justify-center rounded-full hover:bg-brand-cyan/20 transition-colors w-4 h-4"
              aria-label={`Limpar filtro ${label}`}
            >
              <X className="w-3 h-3" />
            </span>
          ) : (
            <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        sideOffset={6}
        className="w-72 p-0 bg-surface-overlay border border-subtle"
        data-testid={`${testId}-popover`}
      >
        {searchable && (
          <div className="px-3 pt-3 pb-2 border-b border-subtle">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
              <Input
                type="search"
                inputMode="search"
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-8 h-8 text-caption"
                aria-label={`Buscar ${label}`}
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-caption text-foreground-muted text-center">
              Nenhum item encontrado
            </div>
          ) : (
            filtered.map(opt => {
              const isOn = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 text-caption transition-colors text-left",
                    isOn ? "bg-brand-cyan/10 text-foreground" : "text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
                  )}
                  data-testid={`${testId}-option-${opt.value}`}
                >
                  <span className={cn(
                    "w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors",
                    isOn ? "bg-brand-cyan border-brand-cyan" : "border-subtle",
                  )}>
                    {isOn && <Check className="w-3 h-3 text-surface-canvas" aria-hidden="true" />}
                  </span>
                  {opt.icon && (
                    <span className="text-base leading-none" aria-hidden="true">{opt.icon}</span>
                  )}
                  <span className="flex-1 truncate">{opt.label || opt.value}</span>
                  {opt.tone && (
                    <Badge
                      variant={opt.tone === "credit_card" ? "info" : opt.tone === "investment" ? "warning" : "success"}
                      className="text-micro"
                    >
                      {opt.tone === "credit_card" ? "Cartão" : opt.tone === "investment" ? "Investimento" : "Conta"}
                    </Badge>
                  )}
                  {typeof opt.count === "number" && (
                    <span className="text-micro text-foreground-subtle tabular-nums">{opt.count}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
